<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useMapGetter } from 'dashboard/composables/store';
import CampaignsAPI from 'dashboard/api/campaigns';

import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import CampaignMetricsFunnel from './CampaignMetricsFunnel.vue';
import CampaignMessagesList from './CampaignMessagesList.vue';
import { useMessageFormatter } from 'shared/composables/useMessageFormatter';
import { useCampaignTemplates } from 'dashboard/composables/useCampaignTemplates';
import { toParserFormat } from 'dashboard/helper/templateHelper';
import WhatsAppTemplatePreview from 'dashboard/components-next/whatsapp/WhatsAppTemplatePreview.vue';
import DropdownMenu from 'dashboard/components-next/dropdown-menu/DropdownMenu.vue';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const props = defineProps({
  campaign: {
    type: Object,
    required: true,
  },
});

const emit = defineEmits(['close']);

const { formatMessage } = useMessageFormatter();

const store = useStore();
const { t } = useI18n();

const uiFlags = useMapGetter('campaigns/getUIFlags');
const metrics = useMapGetter('campaigns/getCampaignMetrics');
const campaignMessages = useMapGetter('campaigns/getCampaignMessages');
const campaignPreviewTemplateParams = useMapGetter(
  'campaigns/getCampaignPreviewTemplateParams'
);

const isLoading = computed(() => uiFlags.value.isFetchingMetrics);

const inboxIdRef = computed(
  () => props.campaign.inbox?.id || props.campaign.inbox_id
);
const { fetchTemplates } = useCampaignTemplates(inboxIdRef);

const campaignTemplate = computed(() => {
  const templateName = props.campaign.template_params?.name;
  if (!templateName) return null;
  const templates = store.getters['messageTemplates/getMessageTemplates'];
  const template = templates.find(
    tmpl => tmpl.name === templateName && tmpl.inbox_id === inboxIdRef.value
  );
  return template ? toParserFormat(template) : null;
});

const previewProcessedParams = computed(
  () =>
    campaignPreviewTemplateParams.value?.processed_params ||
    props.campaign.template_params?.processed_params
);

const showExportDropdown = ref(false);
const exportMenuItems = [
  {
    label: 'Exportar XLSX',
    action: 'xlsx',
    value: 'xlsx',
    icon: 'i-lucide-file-spreadsheet',
  },
  {
    label: 'Exportar CSV',
    action: 'csv',
    value: 'csv',
    icon: 'i-lucide-file-text',
  },
  {
    label: 'Exportar PDF',
    action: 'pdf',
    value: 'pdf',
    icon: 'i-lucide-file-pdf',
  },
];

const aiSummary = ref('');
const isFetchingSummary = ref(false);

const fetchAISummary = async () => {
  isFetchingSummary.value = true;
  try {
    const response = await CampaignsAPI.summary(props.campaign.id);
    aiSummary.value = response.data.summary;
  } catch {
    aiSummary.value = '';
  } finally {
    isFetchingSummary.value = false;
  }
};

const refreshMetrics = () => {
  store.dispatch('campaigns/fetchMetrics', props.campaign.id);
};

onMounted(() => {
  store.dispatch('campaigns/fetchMetrics', props.campaign.id);
  fetchTemplates();
});

const getExportData = () => {
  return campaignMessages.value.map(msg => ({
    [t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.CONTACT')]: msg.contact?.name || '-',
    [t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.PHONE')]:
      msg.contact?.phone_number || '-',
    [t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.STATUS')]: t(
      `CAMPAIGN.WHATSAPP.ANALYTICS.STATUS.${msg.status?.toUpperCase() || 'SENT'}`
    ),
    [t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.CLICKED')]: msg.clicked_at
      ? new Date(msg.clicked_at).toLocaleString()
      : '-',
    [t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.REPLIED')]: msg.replied_at
      ? new Date(msg.replied_at).toLocaleString()
      : '-',
    [t('CAMPAIGN.WHATSAPP.ANALYTICS.TABLE.ERROR')]: msg.error_message || '-',
  }));
};

const exportXlsx = () => {
  const data = getExportData();
  const worksheet = XLSX.utils.json_to_sheet(data);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, 'Report');
  XLSX.writeFile(workbook, `relatorio_campanha_${props.campaign.id}.xlsx`);
};

const exportCsv = () => {
  const data = getExportData();
  const worksheet = XLSX.utils.json_to_sheet(data);
  const csv = XLSX.utils.sheet_to_csv(worksheet);
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.setAttribute('download', `relatorio_campanha_${props.campaign.id}.csv`);
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
};

const exportPdf = async () => {
  // eslint-disable-next-line new-cap
  const doc = new jsPDF();
  const data = getExportData();
  if (!data || data.length === 0) return;

  const AIRYS_LOGO_SVG = `<svg width="1005" height="217" viewBox="0 0 1005 217" fill="none" xmlns="http://www.w3.org/2000/svg"><path d="M93.0988 35.6511C93.659 35.6511 94.2053 35.8492 94.6358 36.2084C95.284 36.7473 95.8164 37.2632 96.3488 38.0877C102.163 47.0969 106.057 59.3764 111.71 68.7173C113.404 71.5131 114.635 72.9041 118.177 73.3002C128.056 74.4102 140.19 72.1533 150.033 73.291C150.922 73.3923 151.556 73.5581 152.176 74.0694C152.422 74.272 152.616 74.53 152.76 74.8155L182.926 135.061C183.194 135.596 183.264 136.213 183.083 136.779C182.68 138.037 181.551 139.188 180.287 139.414C180.125 139.442 179.958 139.437 179.796 139.423C168.907 138.493 155.996 140.667 145.394 139.432C144.829 139.368 144.343 139.276 143.889 139.064C143.436 138.852 143.065 138.47 142.839 138.018L114.344 82.1712C112.839 79.1682 109.478 78.7076 107.7 81.7106L79.4371 138.613C79.2056 139.078 79.1269 139.603 79.2287 140.11C79.6037 141.924 81.1083 142.887 83.0898 143.108C102.261 144.388 123.756 141.367 142.653 143.076C144.274 143.223 145.413 143.472 146.186 145.075C150.042 154.904 157.537 165.06 161.083 174.788C162.07 177.491 161.912 178.744 159.644 180.186C159.269 180.425 158.829 180.545 158.384 180.545L96.0802 180.651C95.7562 180.651 95.4275 180.591 95.1312 180.458C93.5062 179.73 92.7979 178.012 91.9414 176.543C87.3212 168.588 83.5296 157.105 78.5528 149.92C77.2705 148.069 75.7566 146.963 73.4372 146.747C63.2708 145.802 51.1229 147.912 41.0999 146.784C39.5861 146.613 38.7481 146.097 37.8824 145.227C37.4333 144.775 37.1833 144.172 37.1833 143.546V141.832C37.1833 141.454 37.2713 141.086 37.4472 140.75L90.9739 36.9454C91.3813 36.1532 92.2007 35.6557 93.0988 35.6557V35.6511Z" fill="url(#paint0_linear_4017_18)"/><path d="M238.687 166L284.066 52.9731H300.093L345.472 166H327.759L317.806 140.527H266.353L256.231 166H238.687ZM272.258 125.513H311.901L292.164 74.735L272.258 125.513ZM362.733 46.2252C365.657 46.2252 368.075 47.1812 369.987 49.0931C371.899 51.005 372.855 53.423 372.855 56.347C372.855 59.2711 371.899 61.6891 369.987 63.601C368.075 65.5129 365.657 66.4689 362.733 66.4689C359.809 66.4689 357.391 65.5129 355.479 63.601C353.567 61.6891 352.611 59.2711 352.611 56.347C352.611 53.423 353.567 51.005 355.479 49.0931C357.391 47.1812 359.809 46.2252 362.733 46.2252ZM355.142 85.0255H370.325V166H355.142V85.0255ZM427.871 91.436C423.598 91.436 419.661 92.167 416.062 93.629C412.464 95.0911 409.315 97.1155 406.615 99.7021C404.029 102.289 402.004 105.382 400.542 108.98C399.08 112.579 398.349 116.516 398.349 120.789V166H389.914V120.789C389.914 115.391 390.87 110.386 392.782 105.775C394.694 101.164 397.337 97.2279 400.711 93.9664C404.085 90.5925 408.078 88.0058 412.689 86.2064C417.3 84.2945 422.361 83.3385 427.871 83.3385V91.436ZM439.713 189.955C443.649 189.955 447.079 189.561 450.004 188.774C452.928 188.099 455.514 186.918 457.764 185.231C460.013 183.544 462.093 181.239 464.005 178.315C465.917 175.503 467.773 171.961 469.572 167.687L473.79 157.903H466.367L434.652 85.0255H443.93L474.802 155.878L505.505 85.0255H514.783L477.501 170.724C475.252 176.009 472.946 180.395 470.585 183.882C468.223 187.368 465.58 190.124 462.656 192.148C459.732 194.285 456.414 195.803 452.703 196.703C448.991 197.603 444.661 198.052 439.713 198.052V189.955ZM584.435 110.161H576C576 107.687 575.438 105.438 574.313 103.413C573.189 101.277 571.558 99.4772 569.421 98.0152C567.397 96.4407 564.979 95.2598 562.167 94.4725C559.356 93.5728 556.263 93.123 552.889 93.123C546.141 93.123 540.63 94.6975 536.357 97.8465C532.083 100.995 529.946 105.1 529.946 110.161C529.946 114.435 531.352 118.034 534.163 120.958C537.088 123.882 540.686 126.525 544.96 128.887C549.234 131.248 553.845 133.554 558.793 135.803C563.854 137.94 568.521 140.358 572.795 143.057C577.069 145.756 580.611 148.962 583.423 152.673C586.347 156.272 587.809 160.714 587.809 166H579.374C579.374 161.726 577.912 158.184 574.988 155.372C572.177 152.448 568.634 149.861 564.36 147.612C560.087 145.25 555.419 143.001 550.358 140.864C545.41 138.615 540.799 136.141 536.525 133.442C532.252 130.742 528.653 127.537 525.729 123.826C522.917 120.002 521.511 115.391 521.511 109.993C521.511 106.281 522.242 102.907 523.704 99.8708C525.279 96.8343 527.472 94.2476 530.283 92.1108C533.095 89.8615 536.413 88.1183 540.237 86.8812C544.06 85.6441 548.278 85.0255 552.889 85.0255C557.5 85.0255 561.717 85.6441 565.541 86.8812C569.477 88.1183 572.851 89.8615 575.663 92.1108C578.475 94.3601 580.611 97.003 582.073 100.04C583.648 103.076 584.435 106.45 584.435 110.161ZM528.259 154.191C530.171 154.191 531.745 154.866 532.983 156.216C534.332 157.453 535.007 159.027 535.007 160.939C535.007 162.851 534.332 164.482 532.983 165.831C531.745 167.068 530.171 167.687 528.259 167.687C526.347 167.687 524.716 167.068 523.367 165.831C522.13 164.482 521.511 162.851 521.511 160.939C521.511 159.027 522.13 157.453 523.367 156.216C524.716 154.866 526.347 154.191 528.259 154.191ZM598.419 109.487C598.419 100.939 599.881 93.123 602.805 86.0377C605.729 78.84 609.777 72.7106 614.951 67.6497C620.237 62.4764 626.478 58.4839 633.676 55.6723C640.874 52.7482 648.803 51.2861 657.462 51.2861C664.21 51.2861 670.508 52.1859 676.356 53.9853C682.317 55.6723 687.715 58.2027 692.551 61.5766C697.387 64.8381 701.549 68.8306 705.035 73.5541C708.634 78.1652 711.445 83.3948 713.47 89.2429H695.757C692.27 82.0452 687.153 76.422 680.405 72.3732C673.77 68.3245 666.122 66.3002 657.462 66.3002C651.277 66.3002 645.541 67.3686 640.255 69.5054C635.082 71.6422 630.583 74.6225 626.76 78.4463C622.936 82.2701 619.955 86.8249 617.819 92.1108C615.794 97.3966 614.782 103.189 614.782 109.487C614.782 115.785 615.794 121.576 617.819 126.862C619.955 132.148 622.936 136.703 626.76 140.527C630.583 144.351 635.082 147.331 640.255 149.468C645.541 151.605 651.277 152.673 657.462 152.673C666.122 152.673 673.77 150.649 680.405 146.6C687.153 142.551 692.27 136.928 695.757 129.73H713.47C711.445 135.578 708.634 140.864 705.035 145.588C701.549 150.199 697.387 154.191 692.551 157.565C687.715 160.827 682.317 163.357 676.356 165.157C670.508 166.843 664.21 167.687 657.462 167.687C648.803 167.687 640.874 166.281 633.676 163.47C626.478 160.545 620.237 156.497 614.951 151.323C609.777 146.15 605.729 140.021 602.805 132.935C599.881 125.85 598.419 118.034 598.419 109.487ZM739.209 52.9731V97.6778C742.471 93.1792 746.688 89.6928 751.861 87.2186C757.035 84.6319 762.827 83.3385 769.237 83.3385C774.636 83.3385 779.584 84.2945 784.083 86.2064C788.694 88.0058 792.686 90.5925 796.06 93.9664C799.434 97.2279 802.021 101.164 803.82 105.775C805.732 110.386 806.688 115.391 806.688 120.789V166H798.253V120.789C798.253 116.516 797.522 112.579 796.06 108.98C794.598 105.382 792.574 102.289 789.987 99.7021C787.4 97.1155 784.251 95.0911 780.54 93.629C776.941 92.167 773.005 91.436 768.731 91.436C764.458 91.436 760.521 92.167 756.922 93.629C753.324 95.0911 750.175 97.1155 747.475 99.7021C744.889 102.289 742.864 105.382 741.402 108.98C739.94 112.579 739.209 116.516 739.209 120.789V166H730.774V52.9731H739.209ZM864.862 93.123C860.251 93.123 855.921 93.9664 851.872 95.6534C847.936 97.2279 844.45 99.5334 841.413 102.57C838.489 105.494 836.184 108.98 834.497 113.029C832.81 117.078 831.966 121.52 831.966 126.356C831.966 131.192 832.81 135.635 834.497 139.683C836.184 143.732 838.489 147.218 841.413 150.142C844.45 153.067 847.936 155.372 851.872 157.059C855.921 158.746 860.251 159.59 864.862 159.59C869.473 159.59 873.803 158.746 877.852 157.059C881.9 155.372 885.387 153.067 888.311 150.142C891.235 147.218 893.541 143.732 895.227 139.683C896.914 135.635 897.758 131.192 897.758 126.356V109.993H906.193V144.069C906.193 148.905 907.542 152.729 910.242 155.541C912.941 158.24 916.652 159.59 921.375 159.59V167.687C915.527 167.687 910.579 166.169 906.53 163.132C902.481 159.983 899.839 155.822 898.601 150.649C894.89 155.934 890.11 160.096 884.262 163.132C878.414 166.169 871.947 167.687 864.862 167.687C859.014 167.687 853.559 166.619 848.498 164.482C843.55 162.345 839.22 159.421 835.509 155.709C831.797 151.998 828.873 147.668 826.737 142.72C824.6 137.659 823.531 132.204 823.531 126.356C823.531 120.508 824.6 115.11 826.737 110.161C828.873 105.1 831.797 100.714 835.509 97.003C839.22 93.2917 843.55 90.3676 848.498 88.2307C853.559 86.0939 859.014 85.0255 864.862 85.0255H935.715V63.0949H944.15V85.0255H971.31V93.123H944.15V132.261C944.15 136.309 944.768 140.021 946.005 143.395C947.355 146.769 949.21 149.636 951.572 151.998C953.934 154.36 956.746 156.216 960.007 157.565C963.381 158.915 967.092 159.59 971.141 159.59V167.687C965.968 167.687 961.188 166.843 956.802 165.157C952.528 163.357 948.817 160.883 945.668 157.734C942.519 154.585 940.045 150.874 938.245 146.6C936.558 142.214 935.715 137.434 935.715 132.261V93.123H864.862Z" fill="black"/><defs><linearGradient id="paint0_linear_4017_18" x1="110.183" y1="35.6511" x2="110.183" y2="180.651" gradientUnits="userSpaceOnUse"><stop stop-color="#B7FF54"/><stop offset="1" stop-color="#1FFCBB"/></linearGradient></defs></svg>`;

  const pngBase64 = await new Promise(resolve => {
    const img = new Image();
    const blob = new Blob([AIRYS_LOGO_SVG], {
      type: 'image/svg+xml;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/png'));
    };
    img.src = url;
  });

  const headers = Object.keys(data[0] || {});
  const rows = data.map(item => headers.map(key => item[key]));

  doc.setFillColor(27, 251, 189);
  doc.rect(0, 0, 210, 25, 'F');

  doc.addImage(pngBase64, 'PNG', 14, 8, 46.3, 10);

  doc.setTextColor(0, 0, 0);
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.text('Relatório da Campanha', 196, 13, { align: 'right' });
  doc.text(props.campaign.title, 196, 18, { align: 'right' });

  let currentY = 35;

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Funil de Entrega', 14, currentY);
  currentY += 8;

  const totalVal = metrics.value.total || 0;
  const deliveredVal = metrics.value.delivered || 0;
  const readVal = metrics.value.read || 0;
  const failedVal = metrics.value.failed || 0;
  const clickedVal = metrics.value.clicked || 0;
  const repliedVal = metrics.value.replied || 0;
  const convertedVal = metrics.value.converted || 0;

  const getRate = (val, div) =>
    div === 0 ? '0%' : Math.round((val / div) * 100) + '%';

  const funnelData = [
    ['Total de envios', `${totalVal}`, ''],
    ['Entregues', `${deliveredVal}`, getRate(deliveredVal, totalVal)],
    ['Lidas', `${readVal}`, getRate(readVal, deliveredVal)],
    ['Clicadas', `${clickedVal}`, getRate(clickedVal, deliveredVal)],
    ['Respondidas', `${repliedVal}`, getRate(repliedVal, deliveredVal)],
    ['Convertidas', `${convertedVal}`, getRate(convertedVal, deliveredVal)],
    ['Falharam', `${failedVal}`, getRate(failedVal, totalVal)],
  ];

  autoTable(doc, {
    body: funnelData,
    startY: currentY,
    theme: 'plain',
    styles: { fontSize: 10, cellPadding: 2 },
    columnStyles: {
      0: { fontStyle: 'bold', cellWidth: 50 },
      1: { cellWidth: 30 },
      2: { cellWidth: 30, textColor: [100, 100, 100] },
    },
  });

  currentY = doc.lastAutoTable.finalY + 15;

  const failedMessages = campaignMessages.value.filter(
    m => m.status === 'failed' || m.error_message
  );
  if (failedMessages.length > 0) {
    doc.setFontSize(14);
    doc.setFont('helvetica', 'bold');
    doc.text('Análise de Falhas (Erros Comuns)', 14, currentY);
    currentY += 8;

    const errorCounts = {};
    failedMessages.forEach(m => {
      const err = m.error_message || 'Erro desconhecido';
      errorCounts[err] = (errorCounts[err] || 0) + 1;
    });

    const getTroubleshooting = err => {
      const e = err.toLowerCase();
      if (e.includes('outside 24'))
        return 'O cliente não interage há mais de 24 horas. Use um template aprovado para iniciar a conversa.';
      if (e.includes('template'))
        return 'O template usado não está aprovado na Meta ou os parâmetros estão incorretos.';
      if (e.includes('number'))
        return 'O número de destino pode ser inválido, mal formatado ou não possui WhatsApp.';
      if (e.includes('rate limit'))
        return 'O limite de mensagens da sua conta foi atingido. Aguarde ou aumente o tier da conta na Meta.';
      if (e.includes('payment') || e.includes('billing'))
        return 'Problema no faturamento da conta Meta (verifique o saldo ou cartão de crédito na Business Manager).';
      if (e.includes('spam') || e.includes('quality'))
        return 'Número sinalizado como spam pela Meta ou a qualidade da conta está baixa.';
      return 'Verifique as configurações do canal e o status do contato no painel da Meta.';
    };

    const errorData = Object.keys(errorCounts).map(err => [
      `${errorCounts[err]}`,
      err,
      getTroubleshooting(err),
    ]);

    errorData.sort((a, b) => parseInt(b[0], 10) - parseInt(a[0], 10));

    autoTable(doc, {
      head: [['Qtd', 'Erro Retornado', 'Como Evitar / Solucionar']],
      body: errorData,
      startY: currentY,
      theme: 'grid',
      headStyles: { fillColor: [240, 240, 240], textColor: [0, 0, 0] },
      styles: { fontSize: 9 },
      columnStyles: {
        0: { cellWidth: 15, halign: 'center' },
        1: { cellWidth: 80 },
        2: { cellWidth: 'auto' },
      },
    });

    currentY = doc.lastAutoTable.finalY + 15;
  }

  doc.setFontSize(14);
  doc.setFont('helvetica', 'bold');
  doc.text('Detalhamento de Contatos', 14, currentY);
  currentY += 8;

  autoTable(doc, {
    head: [headers],
    body: rows,
    startY: currentY,
    theme: 'striped',
    headStyles: {
      fillColor: [27, 251, 189],
      textColor: [0, 0, 0],
      fontStyle: 'bold',
    },
    alternateRowStyles: { fillColor: [238, 253, 248] },
    styles: { fontSize: 8 },
  });

  doc.save(`relatorio_campanha_${props.campaign.id}.pdf`);
};
const handleExportAction = async ({ action }) => {
  showExportDropdown.value = false;
  if (action === 'xlsx') exportXlsx();
  else if (action === 'csv') exportCsv();
  else if (action === 'pdf') await exportPdf();
};
</script>

<template>
  <div class="fixed inset-0 z-50 flex items-center justify-center bg-n-alpha-2">
    <div
      class="relative flex flex-col w-full max-w-7xl max-h-[85vh] bg-white dark:bg-n-solid-2 rounded-xl border border-n-weak dark:border-n-strong shadow-lg"
    >
      <header
        class="flex items-center justify-between flex-shrink-0 px-6 py-4 border-b border-n-weak dark:border-n-strong"
      >
        <div class="flex flex-col gap-1">
          <h3 class="text-base font-medium text-n-slate-12">
            {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.TITLE') }}
          </h3>
          <span class="text-sm text-n-slate-11">
            {{ campaign.title }}
          </span>
        </div>
        <div class="flex items-center gap-2">
          <div class="relative flex items-center">
            <Button
              label="Exportar"
              variant="faded"
              size="sm"
              color="slate"
              trailing-icon
              icon="i-lucide-download"
              @click="showExportDropdown = !showExportDropdown"
            />
            <DropdownMenu
              v-if="showExportDropdown"
              v-on-clickaway="() => (showExportDropdown = false)"
              :menu-items="exportMenuItems"
              class="mt-1 ltr:right-0 rtl:left-0 top-full"
              @action="handleExportAction"
            />
          </div>
          <Button
            :label="t('CAMPAIGN.WHATSAPP.ANALYTICS.REFRESH')"
            variant="faded"
            size="sm"
            color="slate"
            icon="i-lucide-refresh-cw"
            :is-loading="isLoading"
            @click="refreshMetrics"
          />
          <Button
            v-if="!aiSummary && !isFetchingSummary"
            :label="t('CAMPAIGN.WHATSAPP.ANALYTICS.AI_SUMMARY')"
            variant="faded"
            size="sm"
            color="slate"
            icon="i-lucide-sparkles"
            @click="fetchAISummary"
          />
          <Button
            variant="ghost"
            size="sm"
            color="slate"
            icon="i-lucide-x"
            @click="emit('close')"
          />
        </div>
      </header>

      <div
        v-if="isLoading"
        class="flex items-center justify-center flex-1 py-16"
      >
        <Spinner />
      </div>
      <div v-else class="p-6 overflow-y-auto">
        <div class="grid grid-cols-1 xl:grid-cols-3 gap-6">
          <div class="flex flex-col gap-6 xl:col-span-2">
            <div
              v-if="isFetchingSummary"
              class="flex items-center gap-2 p-4 rounded-lg border border-n-weak dark:border-n-strong bg-n-alpha-1 dark:bg-n-alpha-2"
            >
              <Spinner class="w-4 h-4" />
              <span class="text-sm text-n-slate-11">
                {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.GENERATING_SUMMARY') }}
              </span>
            </div>
            <div
              v-else-if="aiSummary"
              class="p-4 rounded-lg border border-n-weak dark:border-n-strong bg-n-alpha-1 dark:bg-n-alpha-2"
            >
              <div class="flex items-center gap-2 mb-2">
                <span class="text-sm i-lucide-sparkles text-n-violet-11" />
                <h4 class="text-sm font-medium text-n-slate-12">
                  {{ t('CAMPAIGN.WHATSAPP.ANALYTICS.AI_INSIGHTS') }}
                </h4>
              </div>
              <div
                v-dompurify-html="aiSummary"
                class="text-sm text-n-slate-11 prose prose-sm max-w-none"
              />
            </div>

            <CampaignMetricsFunnel :metrics="metrics" />
            <CampaignMessagesList :messages="campaignMessages" />
          </div>

          <div class="flex flex-col gap-4 xl:col-span-1 min-w-0">
            <h4 class="text-sm font-medium text-n-slate-12">
              {{
                t('CAMPAIGN.WHATSAPP.ANALYTICS.PREVIEW', 'Preview da Mensagem')
              }}
            </h4>
            <div
              class="bg-[#efeae2] dark:bg-[#0b141a] p-4 flex flex-col gap-2 rounded-xl shadow-inner h-full min-h-[300px] overflow-y-auto w-full relative"
            >
              <!-- Chat pattern overlay -->
              <div
                class="absolute inset-0 opacity-[0.06] dark:opacity-[0.03] pointer-events-none bg-[radial-gradient(#000_1px,transparent_0)] bg-[size:10px_10px]"
              />

              <!-- WhatsApp Bubble -->
              <WhatsAppTemplatePreview
                v-if="campaignTemplate"
                :template="campaignTemplate"
                :processed-params="previewProcessedParams"
                class="self-end max-w-[90%] z-10"
              />
              <div
                v-else
                class="bg-[#d9fdd3] dark:bg-[#005c4b] text-[#111b21] dark:text-[#e9edef] p-2.5 rounded-lg rounded-tr-none shadow-sm relative max-w-[90%] self-end z-10 flex flex-col"
              >
                <div
                  v-dompurify-html="
                    formatMessage(campaign.message, false, false, false)
                  "
                  class="text-[14.2px] leading-[19px] whitespace-pre-wrap break-words prose prose-sm dark:prose-invert prose-p:my-1 prose-a:text-blue-500 max-w-none"
                />
                <div
                  class="flex justify-end items-center gap-1 mt-1 -mb-1 -mr-1"
                >
                  <span
                    class="text-[11px] text-[#667781] dark:text-[#8696a0] leading-none"
                  >
                    {{
                      new Date().toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })
                    }}
                  </span>
                  <span class="i-lucide-check-check size-3.5 text-[#53bdeb]" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>
